import type { SpaceBackground } from '../types/spaces';
import np1Light from '../assets/bg_images/thumbs/np1-light.webp';
import np1Dark from '../assets/bg_images/thumbs/np1-dark.webp';
import np2Light from '../assets/bg_images/thumbs/np2-light.webp';
import np2Dark from '../assets/bg_images/thumbs/np2-dark.webp';
import np3Light from '../assets/bg_images/thumbs/np3-light.webp';
import np3Dark from '../assets/bg_images/thumbs/np3-dark.webp';
import np4Light from '../assets/bg_images/thumbs/np4-light.webp';
import np4Dark from '../assets/bg_images/thumbs/np4-dark.webp';
import np5Light from '../assets/bg_images/thumbs/np5-light.webp';
import np5Dark from '../assets/bg_images/thumbs/np5-dark.webp';
import np6Light from '../assets/bg_images/thumbs/np6-light.webp';
import np6Dark from '../assets/bg_images/thumbs/np6-dark.webp';
import np7Light from '../assets/bg_images/thumbs/np7-light.webp';
import np7Dark from '../assets/bg_images/thumbs/np7-dark.webp';
import np8Light from '../assets/bg_images/thumbs/np8-light.webp';
import np8Dark from '../assets/bg_images/thumbs/np8-dark.webp';

/**
 * National-park wallpaper presets. Each slot has a light (day) and dark (night)
 * image; CSS `--bg-np-N` swaps with `.theme-scheme-dark` on <html>.
 *
 * np1 is the product default for newly created desktops:
 *   light — des-recits (B8n95nMCsnY)
 *   dark  — casey-horner (O0R5XZfKUGQ)
 *
 * Must match `DEFAULT_SPACE_BACKGROUND_PRESET` in `backend/config/constants.py`
 * and the `.npN` / `--bg-np-N` rules in App.scss + GUILayout.scss.
 */
export const DEFAULT_NEW_SPACE_BACKGROUND_PRESET_ID = 'np1' as const;

export const DEFAULT_NEW_SPACE_BACKGROUND_PRESET_LABEL = 'Default';

export const NATIONAL_PARK_PRESET_COUNT = 8;

export const NATIONAL_PARK_PRESET_LABELS: Record<string, string> = {
  np1: 'Default',
  np2: 'Yosemite',
  np3: 'Grand Teton',
  np4: 'Grand Canyon',
  np5: 'Banff',
  np6: 'Arches',
  np7: 'Glacier',
  np8: 'Yellowstone',
};

export const NATIONAL_PARK_PRESET_THUMBS: Record<'light' | 'dark', Record<number, string>> = {
  light: {
    1: np1Light, 2: np2Light, 3: np3Light, 4: np4Light,
    5: np5Light, 6: np6Light, 7: np7Light, 8: np8Light,
  },
  dark: {
    1: np1Dark, 2: np2Dark, 3: np3Dark, 4: np4Dark,
    5: np5Dark, 6: np6Dark, 7: np7Dark, 8: np8Dark,
  },
};

export const DEFAULT_NEW_SPACE_BACKGROUND: SpaceBackground = {
  type: 'preset',
  value: DEFAULT_NEW_SPACE_BACKGROUND_PRESET_ID,
};

export const DEFAULT_DESKTOP_BACKGROUND: SpaceBackground = DEFAULT_NEW_SPACE_BACKGROUND;

/**
 * Product default wallpaper when a space has no `settings.background`.
 * Explicit "No background" is stored as `{ type: 'color', ... }` — that is
 * not missing, and must not be replaced.
 */
export function resolveEffectiveSpaceBackground(
  background?: SpaceBackground | null,
): SpaceBackground {
  if (!background) return DEFAULT_NEW_SPACE_BACKGROUND;
  return background;
}

export function isPresetSwatchActive(
  background: SpaceBackground | null | undefined,
  presetId: string,
): boolean {
  if (!background) return presetId === DEFAULT_NEW_SPACE_BACKGROUND_PRESET_ID;
  return background.type === 'preset' && background.value === presetId;
}

export function isNoBackgroundActive(
  background?: SpaceBackground | null,
): boolean {
  return background?.type === 'color';
}

/** CSS custom-property for a stored preset id (`bg3` or `np1`). */
export function presetBackgroundCssVar(value?: string): string | null {
  if (!value) return null;
  const bg = value.match(/^bg(\d+)$/);
  if (bg) return `var(--bg-${bg[1]})`;
  const np = value.match(/^np(\d+)$/);
  if (np) return `var(--bg-np-${np[1]})`;
  return null;
}
